"use client";
import type { PostHog } from "posthog-js";

import type {
  IAnalyticsProvider,
  AnalyticsIdentifyPayload,
} from "@repo/analytics/client";
import { analyticsIdentifyName } from "@repo/analytics/utils";
import { setPosthogSupportIdentity } from "@repo/analytics/lib/posthog-support";
import {
  useEffect,
  useRef,
  useState,
  useMemo,
  type PropsWithChildren,
} from "react";
import { usePathname } from "next/navigation";

import { analyticsContext } from "@/hooks/use-analytics";
import { AnalyticsPageView } from "@/components/common/analytics-pageview";
import { useCurrentUser } from "@/hooks/use-current-user";
import { createAnalyticsQueue } from "@/lib/analytics-queue";
import { useAfterSellerFormReady } from "@/lib/seller-form-ready";
import { api } from "@/lib/api-client";

export function AnalyticsProvider({ children }: PropsWithChildren) {
  const [analytics, setAnalytics] = useState<IAnalyticsProvider<PostHog>>();
  const pathname = usePathname();
  const ready = useAfterSellerFormReady();
  const queue = useMemo(createAnalyticsQueue, []);
  const previousIdentity = useRef<string | null>(null);

  const { user, loaded } = useCurrentUser();
  const identityQuery = api.user.getPosthogIdentity.useQuery(undefined, {
    enabled: loaded && !!user,
    retry: false,
  });

  const providerRef = useRef(analytics);
  providerRef.current = analytics;
  const context = useMemo(
    () => ({
      identify: async (
        distinctId: string,
        properties?: AnalyticsIdentifyPayload,
      ) => {
        await providerRef.current?.identify(distinctId, properties);
      },
      unidentify: async () => {
        await providerRef.current?.unidentify();
      },
      trackEvent: queue.trackEvent,
      trackPageView: queue.trackPageView,
    }),
    [queue],
  );

  useEffect(() => {
    if (!process.env.NEXT_PUBLIC_POSTHOG_KEY || !ready) return;

    import("@repo/analytics/client")
      .then((module) => {
        setAnalytics(module.analytics);
      })
      .catch(() => {
        /* A blocked analytics SDK must never block the form. */
      });
  }, [ready]);

  useEffect(() => {
    if (!analytics) {
      return;
    }

    if (loaded && user) {
      previousIdentity.current = user.id;
      analytics.identify(user.id, {
        email: user.email,
        ...analyticsIdentifyName(user.name),
        roles: user.roles,
        isAgent: user.isAgent,
        isVerified: user.isVerified,
        isStaff: user.isStaff,
        isPasswordless: user.isPasswordless,
        hasPhoto: !!user.image,
        hasBio: !!user.profile?.bio,
        sourcePartner: user.partnerAgents?.[0]?.partner.name ?? undefined,
      });
    } else if (
      loaded &&
      !user &&
      (previousIdentity.current || analytics.rawClient.get_property("$user_id"))
    ) {
      previousIdentity.current = null;
      analytics.unidentify();
    }
  }, [user, loaded, analytics]);

  useEffect(() => {
    if (!analytics || !identityQuery.data?.hash) return;
    setPosthogSupportIdentity(
      analytics.rawClient,
      identityQuery.data.distinctId,
      identityQuery.data.hash,
    );
  }, [analytics, identityQuery.data]);

  useEffect(() => {
    if (
      !analytics ||
      (!pathname.startsWith("/buyer-onboarding/") &&
        !pathname.startsWith("/seller-onboarding/"))
    ) {
      return;
    }

    // Onboarding failures are exactly the sessions we need to inspect. The
    // project-wide sample rate must not drop these sessions.
    analytics.rawClient.startSessionRecording({ sampling: false });
  }, [analytics, pathname]);

  useEffect(() => {
    if (analytics) queue.attach(analytics);
  }, [analytics, queue]);

  return (
    <analyticsContext.Provider value={context}>
      <AnalyticsPageView />
      {children}
    </analyticsContext.Provider>
  );
}
